import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUserById } from '../../../services/users';
import LoadingSpinner from '../../../components/Common/LoadingSpinner';
// Icons for a professional profile feel
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Shield, 
  Calendar, 
  Edit3, 
  User as UserIcon,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const UserDetail = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      const data = await getUserById(id);
      setUser(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>;
  if (!user) return (
    <div className="p-10 text-center bg-white rounded-3xl border border-slate-200">
      <XCircle size={48} className="mx-auto text-rose-500 mb-4" />
      <h2 className="text-xl font-bold">User not found</h2>
      <Link to="/dashboard/org-admin/users" className="text-indigo-600 mt-4 inline-block font-bold">Return to Directory</Link>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-10 space-y-8 animate-in fade-in duration-500">
      {/* --- NAVIGATION --- */}
      <Link 
        to="/dashboard/org-admin/users" 
        className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-bold transition-colors group"
      >
        <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
        Back to Directory
      </Link>

      {/* --- PROFILE HEADER --- */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-100/50 overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-indigo-500 to-purple-600" />
        <div className="px-8 pb-8">
          <div className="relative flex flex-col md:flex-row md:items-end justify-between -mt-12 gap-6">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-6">
              <div className="h-32 w-32 bg-white rounded-3xl p-2 shadow-xl border border-slate-100">
                <div className="h-full w-full bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300">
                  <UserIcon size={48} strokeWidth={1.5} />
                </div>
              </div>
              <div className="text-center md:text-left pb-2">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">{user.full_name}</h1>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                    user.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                  }`}>
                    {user.is_active ? 'Account Active' : 'Account Suspended'}
                  </span>
                  <span className="bg-slate-100 text-slate-500 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200">
                    {user.role?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <Link 
              to={`/dashboard/org-admin/users/${id}/edit`}
              className="flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
            >
              <Edit3 size={18} />
              Edit Profile
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {/* Contact Details */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-2">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 group">
                  <div className="h-10 w-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Email Address</p>
                    <p className="font-bold text-slate-700">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="h-10 w-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Phone Number</p>
                    <p className="font-bold text-slate-700">{user.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* System Details */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-2">System Metadata</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 group">
                  <div className="h-10 w-10 bg-slate-50 text-slate-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Shield size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Access Level</p>
                    <p className="font-bold text-slate-700 uppercase tracking-tighter">{user.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="h-10 w-10 bg-slate-50 text-slate-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Calendar size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Joined Platform</p>
                    <p className="font-bold text-slate-700">{new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- QUICK STATS / CARDS (Optional Placeholder) --- */}
      <div className="bg-indigo-50/50 rounded-[2rem] p-6 border border-indigo-100 flex items-start gap-4">
        <CheckCircle2 className="text-indigo-600 mt-1" />
        <div>
          <p className="font-black text-indigo-900">Account Integrity</p>
          <p className="text-sm text-indigo-700">This user has full access to their assigned administrative units. No security alerts are currently active for this profile.</p>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;